import { Box, Button, Grid, Modal, Tab, Tabs } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFTextField,
} from "../../../../../../../RHFComponents/RHFComponents";
import { PgmtgpsData } from "./SospensioniAgendeModal";
import { Dayjs } from "dayjs";
import { pgmtgps, ricPgmtgps } from "../../../../../../../../networking";
import { MedintDataContext } from "../../../../../../Context/MedintContextWrapper";
import { AllertMessage } from "../MedintAgendaFormDetail";

type Props = {
  open: boolean;
  onClose: () => void;
  parentMethods: UseFormReturn<any>;
  initialData?: PgmtgpsData;
  createdIdPgmedt?: string;
  action: "insert" | "edit" | "copy";
  onSaved?: () => Promise<void> | void;
};

export const toBool01 = (v?: string | number | null | boolean) =>
  String(v ?? "") === "1";

type SospensioniAgendeForm = {
  agendaSospesa: {
    dataSospensione: string | Dayjs;
    sospesa: boolean;
    noteSospensione: string;
    noteAgenda: string;
    descrTipologia: string;
    codiceTpgpre: string;
    pgmedtPrgint: string;
    pgmtgpsPrgint: string;
    pgmtgpsPidAcu: string;
    pgmtgpsSwAltriOrari: string;
    pgmtgpsTimestamp: string;
  };
};

// Stili
export const BLUE = "#003267";

export const sxTabs = {
  mb: 2,
  minHeight: 38,
  "& .MuiTabs-indicator": {
    backgroundColor: BLUE,
    height: 3,
    borderRadius: 3,
  },
  "& .MuiTab-root": {
    minHeight: 38,
    textTransform: "none",
    px: 0,
    mr: 3,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#9e9e9e",
    transition: "color .2s ease",
  },
  "& .MuiTab-root.Mui-selected": { color: BLUE },
} as const;

const SospensioniAgendeModalDetails = (props: Props) => {
  const context = useContext(MedintDataContext);

  const modalMethods = useForm<SospensioniAgendeForm>({
    defaultValues: {
      agendaSospesa: {
        dataSospensione: props.initialData?.PGMTGPS_DATA_SOSPENSIONE ?? "",
        sospesa: toBool01(props.initialData?.PGMTGPS_SW_SOSPESA) ?? false,
        noteSospensione: props.initialData?.PGMTGPS_NOTE_SOSPENSIONE ?? "",
        noteAgenda: props.initialData?.PGMTGPS_NOTA_AGENDA ?? "",
        descrTipologia: props.initialData?.DECODE_TPGPRE_TPGPRE_DESCR ?? "",
        codiceTpgpre: props.initialData?.TPGPRE_CODICE ?? "",
        pgmedtPrgint: props.initialData?.PGMEDT_PRGINT ?? "",
        pgmtgpsPrgint: props.initialData?.PGMTGPS_PRGINT ?? "",
        pgmtgpsPidAcu: props.initialData?.PGMTGPS_PID_ACU ?? "",
        pgmtgpsSwAltriOrari: props.initialData?.PGMTGPS_SW_ALTRI_ORARI ?? "",
        pgmtgpsTimestamp: props.initialData?.PGMTGPS_TIMESTAMP ?? "",
      },
    },
    mode: "onChange",
  });

  const idPgmedt =
    props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
    (props.createdIdPgmedt as string | undefined);

  const [loading, setLoading] = useState<boolean>(false);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!props.open) {
      return;
    }

    if (props.action === "insert") {
      modalMethods.reset({
        agendaSospesa: {
          dataSospensione: "",
          sospesa: false,
          noteSospensione: "",
          noteAgenda: "",
          descrTipologia: "",
          codiceTpgpre: "",
          pgmedtPrgint: "",
          pgmtgpsPrgint: "",
          pgmtgpsPidAcu: "",
          pgmtgpsSwAltriOrari: "",
          pgmtgpsTimestamp: "",
        },
      });
      return;
    }

    modalMethods.reset({
      agendaSospesa: {
        dataSospensione: props.initialData?.PGMTGPS_DATA_SOSPENSIONE ?? "",
        sospesa: toBool01(props.initialData?.PGMTGPS_SW_SOSPESA) ?? false,
        noteSospensione: props.initialData?.PGMTGPS_NOTE_SOSPENSIONE ?? "",
        noteAgenda: props.initialData?.PGMTGPS_NOTA_AGENDA ?? "",
        descrTipologia: `${props.initialData?.DECODE_TPGPRE_TPGPRE_DESCR ?? ""}`,
        codiceTpgpre: props.initialData?.TPGPRE_CODICE ?? "",
        pgmedtPrgint: props.initialData?.PGMEDT_PRGINT ?? "",
        pgmtgpsPrgint: props.initialData?.PGMTGPS_PRGINT ?? "",
        pgmtgpsPidAcu: props.initialData?.PGMTGPS_PID_ACU ?? "",
        pgmtgpsSwAltriOrari: props.initialData?.PGMTGPS_SW_ALTRI_ORARI ?? "",
        pgmtgpsTimestamp: props.initialData?.PGMTGPS_TIMESTAMP ?? "",
      },
    });
  }, [props.open, props.action, props.initialData, modalMethods]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = () => {
    setMessage(
      props.action === "insert"
        ? "Confermare l'inserimento della sospensione?"
        : "Confermare la modifica della sospensione?",
    );
    setSeverity("warning");
    setShowAllert(true);
  };

  const handleSubmit = async (data: SospensioniAgendeForm) => {
    setLoading(true);
    console.log({ data });
    const isInsert = props.action === "insert";
    const values = data.agendaSospesa;
    try {
      const response = await pgmtgps({
        crud: props.action === "insert" ? "c" : "u",
        //  prgint: values.pgmtgpsPrgint,
        ...(!isInsert ? { prgint: values.pgmtgpsPrgint } : {}),
        dati: {
          pgmtgps: {
            PGMEDT_PRGINT: idPgmedt,
            PGMTGPS_DATA_SOSPENSIONE: values.dataSospensione,
            PGMTGPS_SW_SOSPESA: values.sospesa ? "1" : "0",
            PGMTGPS_NOTE_SOSPENSIONE: values.noteSospensione,
            PGMTGPS_NOTA_AGENDA: values.noteAgenda,
            PGMTGPS_SW_ALTRI_ORARI: values.pgmtgpsSwAltriOrari,
            PGMTGPS_PID_ACU: values.pgmtgpsPidAcu,
            TPGPRE_CODICE: values.codiceTpgpre,
          },
        },
      });
      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage(response?.data?.messaggio ?? "");
        setTimeout(() => props.onClose(), 800);
      } else {
        setSeverity("error");
        setMessage(response?.data?.messaggio ?? "");
        return;
      }
      await props?.onSaved?.();
      setLoading(false);
      props.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={() => modalMethods.handleSubmit(handleSubmit)()}
      />
      <Modal
        open={props.open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          console.log("Chiudo");

          props.onClose();
        }}
      >
        <Box
          sx={{
            width: "92vw",
            maxWidth: 980,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 2,
          }}
        >
          <Tabs
            value={0}
            sx={{ px: 1, ...sxTabs }}
            TabIndicatorProps={{ sx: { height: 3 } }}
          >
            <Tab
              label="Sospensioni Agende - Dettaglio"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
              }}
            />
          </Tabs>
          <Grid container spacing={2} p={1}>
            <Grid item xs={12} md={6}>
              <RHFTextField
                control={props.parentMethods.control}
                name="medIntNuovoProgramma.codProg"
                label="Programma"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                control={modalMethods.control}
                name="agendaSospesa.codiceTpgpre"
                label="Tipologia"
                options={context?.typologiesTIPOLOGIE ?? []}
                getOptionLabel={(t: any) =>
                  `${String(t.CODICE ?? "").trim()} - ${String(t.DESCRIZIONE ?? "").trim()}`
                }
                mapIn={(code, opts) =>
                  opts.find(
                    (o: any) => String(o.CODICE).trim() === String(code).trim(),
                  ) ?? null
                }
                mapOut={(opt: any) => String(opt?.CODICE ?? "")}
                fullWidth
                disabled={props.action === "edit"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFCheckbox
                control={modalMethods.control}
                name="agendaSospesa.sospesa"
                label="Sospesa"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFDateTimePicker
                control={modalMethods.control}
                //Da cambiare
                name="agendaSospesa.dataSospensione"
                label="Data Sospensione"
                fullWidth
                dateOnly
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <RHFTextField
                control={modalMethods.control}
                //Da cambiare
                name="agendaSospesa.noteSospensione"
                label="Note sospensione"
                placeholder="Note eventuali"
                rows={6}
                multiline
                inputProps={{ maxLength: 400 }}
                helperText={
                  (modalMethods.watch("agendaSospesa.noteSospensione")
                    ?.length ?? 0) + "/400"
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <RHFTextField
                control={modalMethods.control}
                //Da cambiare
                name="agendaSospesa.noteAgenda"
                label="Note agenda"
                rows={6}
                multiline
                placeholder="Note eventuali"
                inputProps={{ maxLength: 400 }}
                helperText={
                  (modalMethods.watch("agendaSospesa.noteAgenda")?.length ??
                    0) + "/400"
                }
                fullWidth
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              display={"flex"}
              justifyContent={"space-between"}
            >
              <Button variant="outlinedCustom" onClick={props.onClose}>
                Indietro
              </Button>
              <Button variant="containedCustom" onClick={askConfirmSubmit}>
                Conferma
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default SospensioniAgendeModalDetails;
